import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import moment from "moment";
import * as React from "react";
import {useCallback} from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {
    MediaLayout,
    Strut,
    type MediaSize,
} from "@khanacademy/wonder-blocks-layout";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    LabelLarge,
    LabelXSmall,
    styles as typographyStyles,
} from "@khanacademy/wonder-blocks-typography";
import {
    DEFAULT_TIME,
    getDay,
    getTime,
    updateDay,
    updateTime,
} from "@khan/assignments-utils";
import {MaybeNativeDatePicker} from "@khan/component-date-picker";
import {TimePicker} from "@khan/component-time-picker";
import {mediaQueries} from "@khan/shared-styles";

import warningIcon from "@phosphor-icons/core/fill/warning-fill.svg";

const HTML_TIME_FORMAT = "HH:mm:ss";
const MOMENT_TIME_FORMAT = "h:mm A";

/**
 * Default width (in case we don't provide one via props)
 */
const FIELD_WIDTH = 244;
const FIELD_HEIGHT = 40;

interface Props {
    /**
     * Whether both inputs/controls are disabled.
     */
    disabled?: boolean;
    /**
     * Whether the date field is disabled.
     */
    dateDisabled?: boolean;
    /**
     * Whether the time field is disabled.
     */
    timeDisabled?: boolean;
    /**
     * The date assigned.
     */
    currentDate: Date | null | undefined;
    /**
     * The maximum date allowed to set in the DateField.
     */
    maxDate?: moment.Moment;
    /**
     * The minimum date allowed to set in the DateField.
     */
    minDate?: moment.Moment;
    /**
     * Called when the current date/time has changed.
     */
    onDateChanged: (newDate: Date) => unknown;
    /**
     * The label assigned to the date field
     */
    dateLabel?: string;
    /**
     * The placeholder assigned to the date field
     */
    datePlaceholder?: string;
    /**
     * The optional date tooltip added to the date field.
     * NOTE: This prop receives a React node that can contain an extra element
     * to be used next to the date label. It usually expects a combination of
     * Tooltip + Icon.
     */
    dateLabelTooltip?: React.ReactNode;
    /**
     * The time that will be passed to the date in case there's no time set.
     */
    defaultTime?: string;
    /**
     * A warning message to display below the date field.
     */
    dateWarningMessage?: string | null;
    /**
     * The label assigned to the time field
     */
    timeLabel?: string;
    /**
     * The placeholder assigned to the time field
     */
    timePlaceholder?: string;
    /**
     * The optional tooltip added to the time field.
     * NOTE: This prop receives a React node that can contain an extra element
     * to be used next to the time label. It usually expects a combination of
     * Tooltip + Icon.
     */
    timeLabelTooltip?: React.ReactNode;
    /**
     * Test ID used for e2e/unit testing.
     *
     * NOTE: This value will be used and modified internally for different
     * components:
     *
     * @example: testId="field"
     *  - DateTimeFieldset wrapper: "field"
     *  - DatePicker input: "field-date-input"
     *  - TimePicker input: "field-time-input"
     */
    testId?: string;
    /**
     * An optional width to be applied in both fields.
     */
    width?: number;
    /**
     * An optional style to be applied to the fieldset container
     */
    style?: StyleType;
    /**
     * If this is a required field, add a red asterisk next to the label.
     * This is only used for the label, not the input itself.
     * Defaults to false.
     */
    required?: boolean;
}

/**
 * A component that handles a group of Date and Time fields that are connected
 * to the same input/value.
 *
 * This component is in charge of handling any interactions and notifying it to
 * the parent component via the `onDateChanged` prop.
 */
const DateTimeFieldset = (props: Props) => {
    const {
        disabled,
        dateDisabled,
        timeDisabled,
        currentDate,
        maxDate,
        minDate = moment(),
        onDateChanged,
        dateLabel = "",
        datePlaceholder,
        dateLabelTooltip,
        defaultTime = DEFAULT_TIME,
        dateWarningMessage = null,
        timeLabel = "",
        timePlaceholder,
        timeLabelTooltip,
        testId,
        width = FIELD_WIDTH,
        style,
        required = false,
    } = props;

    /**
     * Called when the DATE has changed using the DatePicker component.
     * @param {moment$Moment} selectedDate - The selected date.
     */
    const handleDateChange = useCallback(
        (selectedDate: moment.Moment) => {
            // If the date is invalid (e.g. empty), then we assign the default time
            // to the new date. Otherwise we add the new date to the current one.
            const newDate = !currentDate
                ? updateTime(selectedDate.toDate(), defaultTime)
                : updateDay(currentDate, selectedDate.toDate());

            onDateChanged(newDate);
        },
        [currentDate, defaultTime, onDateChanged],
    );

    /**
     * Called when the TIME has changed using the TimePicker component.
     * @param {string} selectedTime - The selected time.
     */
    const handleTimeChange = useCallback(
        (selectedTime: string) => {
            if (!currentDate) {
                return;
            }

            // If the time is invalid (e.g. empty), then we need to pass the default
            // time to the current date.
            if (!selectedTime) {
                selectedTime = DEFAULT_TIME;
            }

            const newDate = updateTime(currentDate, selectedTime);

            onDateChanged(newDate);
        },
        [currentDate, onDateChanged],
    );

    /**
     * Called when the TIME value has changed using the native input element.
     * @param {SyntheticInputEvent} e
     *
     * NOTE: This is used in Mobile web.
     */
    const handleInputTimeChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (!currentDate) {
                return;
            }

            const selectedTime = e.target.value;

            const formattedDueTime = !selectedTime
                ? // If the time is invalid (e.g. empty), then we need to pass the default
                  // time to the current date.
                  DEFAULT_TIME // DEFAULT_TIME is already in the correct format
                : moment(selectedTime, HTML_TIME_FORMAT).format(
                      MOMENT_TIME_FORMAT,
                  );

            const newDate = updateTime(currentDate, formattedDueTime);

            onDateChanged(newDate);
        },
        [currentDate, onDateChanged],
    );

    const maybeRenderWarning = (): React.ReactNode => {
        if (!dateWarningMessage) {
            return null;
        }

        return (
            <View style={styles.warning} testId={testId && `${testId}-warning`}>
                <PhosphorIcon
                    icon={warningIcon}
                    color={semanticColor.core.foreground.warning.subtle}
                    size="medium"
                    style={{marginInlineEnd: spacing.xxSmall_6}}
                />
                <LabelXSmall>{dateWarningMessage}</LabelXSmall>
            </View>
        );
    };

    const renderDateField = (mediaSize: MediaSize): React.ReactNode => {
        const fieldId = testId && `${testId}-date`;

        return (
            <View style={styles.field}>
                {dateLabelTooltip ? (
                    <View style={styles.labelWrapper}>
                        <LabelLarge tag="label" htmlFor={fieldId}>
                            {dateLabel || t`Date`}
                        </LabelLarge>
                        {required && (
                            <>
                                <Strut size={spacing.xSmall_8} />
                                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- TODO: Address a11y error */}
                                <LabelLarge
                                    tag="label"
                                    style={{
                                        color: semanticColor.core.foreground
                                            .critical.subtle,
                                    }}
                                >
                                    *
                                </LabelLarge>
                            </>
                        )}
                        <Strut size={spacing.xSmall_8} />
                        {dateLabelTooltip}
                    </View>
                ) : (
                    <View style={styles.labelWrapper}>
                        <LabelLarge tag="label" htmlFor={fieldId}>
                            {dateLabel || t`Date`}
                        </LabelLarge>
                        {required && (
                            <>
                                <Strut size={spacing.xSmall_8} />
                                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- TODO: Address a11y error */}
                                <LabelLarge
                                    tag="label"
                                    style={{
                                        color: semanticColor.core.foreground
                                            .critical.subtle,
                                    }}
                                >
                                    *
                                </LabelLarge>
                            </>
                        )}
                    </View>
                )}
                <Strut size={spacing.xSmall_8} />
                <MaybeNativeDatePicker
                    dateFormat="MMMM D"
                    disabled={disabled || dateDisabled}
                    minDate={minDate}
                    maxDate={maxDate ?? moment().add(2, "years")}
                    placeholder={datePlaceholder}
                    selectedDate={currentDate && moment(getDay(currentDate))}
                    style={{width: width, height: FIELD_HEIGHT}}
                    updateDate={(d) => {
                        d && handleDateChange(d);
                    }}
                    id={fieldId}
                />
                {maybeRenderWarning()}
            </View>
        );
    };

    const renderTimeFieldDesktop = (): React.ReactNode => {
        const timeTestId = testId ? `${testId}-time` : undefined;

        return (
            <TimePicker
                disabled={disabled || timeDisabled}
                onChange={handleTimeChange}
                placeholder={timePlaceholder}
                testId={timeTestId}
                value={currentDate && getTime(currentDate)}
                width={width}
            />
        );
    };

    const renderTimeFieldMobile = (): React.ReactNode => {
        // NOTE: We force the locale to English to avoid having issues with
        // languages that doesn't support the default format.
        const selectedValue = currentDate
            ? moment(getTime(currentDate), MOMENT_TIME_FORMAT)
                  .locale("en")
                  .format(HTML_TIME_FORMAT)
            : "";

        return (
            <input
                type="time"
                className={css(styles.mobileDateTimeInput)}
                value={selectedValue}
                onChange={handleInputTimeChange}
                placeholder={timeLabel || t`Time`}
                disabled={disabled || timeDisabled}
                style={{width: width, height: FIELD_HEIGHT}}
            />
        );
    };

    const renderTimeField = (mediaSize: MediaSize): React.ReactNode => {
        const fieldId = testId ? `${testId}-time` : undefined;

        return (
            <View style={styles.field}>
                {timeLabelTooltip ? (
                    <View style={styles.labelWrapper}>
                        <LabelLarge tag="label" htmlFor={fieldId}>
                            {timeLabel || t`Time`}
                        </LabelLarge>
                        {required && (
                            <>
                                <Strut size={spacing.xSmall_8} />
                                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- TODO: Address a11y error */}
                                <LabelLarge
                                    tag="label"
                                    style={{
                                        color: semanticColor.core.foreground
                                            .critical.subtle,
                                    }}
                                >
                                    *
                                </LabelLarge>
                            </>
                        )}
                        <Strut size={spacing.xSmall_8} />
                        {timeLabelTooltip}
                    </View>
                ) : (
                    <View style={styles.labelWrapper}>
                        <LabelLarge tag="label" htmlFor={fieldId}>
                            {timeLabel || t`Time`}
                        </LabelLarge>
                        {required && (
                            <>
                                <Strut size={spacing.xSmall_8} />
                                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- TODO: Address a11y error */}
                                <LabelLarge
                                    tag="label"
                                    style={{
                                        color: semanticColor.core.foreground
                                            .critical.subtle,
                                    }}
                                >
                                    *
                                </LabelLarge>
                            </>
                        )}
                    </View>
                )}
                <Strut size={spacing.xSmall_8} />
                {mediaSize === "small"
                    ? renderTimeFieldMobile()
                    : renderTimeFieldDesktop()}
            </View>
        );
    };

    return (
        <View style={[styles.container, style]} testId={testId}>
            <MediaLayout>
                {({mediaSize}) => (
                    <>
                        {renderDateField(mediaSize)}

                        <Strut size={spacing.large_24} style={styles.spacer} />

                        {renderTimeField(mediaSize)}
                    </>
                )}
            </MediaLayout>
        </View>
    );
};

const styles = StyleSheet.create({
    labelWrapper: {
        flexDirection: "row",
        alignItems: "center",
    },
    mobileDateTimeInput: {
        [mediaQueries.smOrSmaller]: {
            padding: "6px 10px 5px",
            border: `1px solid ${semanticColor.core.border.neutral.default}`,
            borderRadius: 4,
            ...typographyStyles.Body,
            boxSizing: "border-box",
        },
    },
    container: {
        flexDirection: "row",

        [mediaQueries.smOrSmaller]: {
            flexDirection: "column",
        },
    },
    field: {
        marginBlockEnd: spacing.large_24,
        maxWidth: "100%",
    },
    spacer: {
        [mediaQueries.smOrSmaller]: {
            // On mobile, we stack the two "columns", and so we don't need
            // any horizontal space between them.
            display: "none",
        },
    },
    warning: {
        alignItems: "center",
        flexDirection: "row",
        marginBlockStart: spacing.xxSmall_6,
    },
});

export default DateTimeFieldset;
